import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  createCampaignMutationOptions,
  deleteCampaignAnalysisMutationOptions,
  deleteCampaignMutationOptions,
  endCampaignMutationOptions,
  excludeCampaignPostsMutationOptions,
  executeCampaignAnalysisMutationOptions,
  invalidateCampaignQueries,
  renameCampaignAnalysisMutationOptions,
  renameCampaignMutationOptions,
  updateCampaignCreatorLabelMutationOptions,
  updateCampaignMutationOptions,
  type InvalidateCampaignQueriesOptions,
} from '@/entities/campaign';
import { useQueryKeys } from '@/shared/hooks/use-query-keys';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { toastRuntimeError } from '@/shared/utils';

export function useInvalidateAllCampaigns() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();

  return (options?: InvalidateCampaignQueriesOptions) =>
    invalidateCampaignQueries(queryClient, workspaceId, options);
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  const options = createCampaignMutationOptions(queryClient, workspaceId);

  return useMutation({
    ...options,
    onError: (error, variables, onMutateResult, context) => {
      options.onError?.(error, variables, onMutateResult, context);
      toastRuntimeError(error, 'Failed to create campaign. Please try again.');
    },
  });
}

export function useDeleteCampaignMutation() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  const options = deleteCampaignMutationOptions(queryClient, workspaceId);

  return useMutation({
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await options.onSuccess?.(data, variables, onMutateResult, context);
      toast.success('Campaign deleted.');
    },
    onError: (error, variables, onMutateResult, context) => {
      options.onError?.(error, variables, onMutateResult, context);
      toast.error('Failed to delete campaign. Please try again.');
    },
  });
}

export function useEndCampaignMutation() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  const options = endCampaignMutationOptions(queryClient, workspaceId);

  return useMutation({
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await options.onSuccess?.(data, variables, onMutateResult, context);
      toast.success('Campaign will end tomorrow.');
    },
    onError: (error, variables, onMutateResult, context) => {
      options.onError?.(error, variables, onMutateResult, context);
      toast.error('Failed to end campaign. Please try again.');
    },
  });
}

export function useRenameCampaignMutation() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  const options = renameCampaignMutationOptions(queryClient, workspaceId);

  return useMutation({
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await options.onSuccess?.(data, variables, onMutateResult, context);
      toast.success('Campaign name updated.');
    },
    onError: (error, variables, onMutateResult, context) => {
      options.onError?.(error, variables, onMutateResult, context);
      toastRuntimeError(error, 'Failed to rename campaign. Please try again.');
    },
  });
}

export function useUpdateCampaignMutation() {
  const options = updateCampaignMutationOptions();

  return useMutation({
    ...options,
    onError: (error, variables, onMutateResult, context) => {
      options.onError?.(error, variables, onMutateResult, context);
      toastRuntimeError(error, 'Failed to update campaign. Please try again.');
    },
  });
}

export function useExcludeCampaignPostsMutation() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  return useMutation(excludeCampaignPostsMutationOptions(queryClient, workspaceId));
}

export function useExecuteCampaignAnalysisMutation() {
  const queryClient = useQueryClient();
  const queryKeys = useQueryKeys();
  const { workspaceId } = useWorkspaceId();
  return useMutation(
    executeCampaignAnalysisMutationOptions(queryClient, workspaceId, queryKeys.workspace.usage()),
  );
}

export function useDeleteCampaignAnalysisMutation() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  return useMutation(deleteCampaignAnalysisMutationOptions(queryClient, workspaceId));
}

export function useRenameCampaignAnalysisMutation() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  return useMutation(renameCampaignAnalysisMutationOptions(queryClient, workspaceId));
}

export function useUpdateCampaignCreatorLabelMutation() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  return useMutation(updateCampaignCreatorLabelMutationOptions(queryClient, workspaceId));
}
